import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    setVendorsReducer,
    addVendorReducer,
    removeVendorReducer,
    updateVendorReducer,
    setCurrentVendorReducer,
} from "../reducers";

const loadVendorSession = () => {
    try {
        const token = localStorage.getItem("vendor_token");
        const vendor = JSON.parse(localStorage.getItem("vendor_data") || "null");
        if (token && vendor) return { authVendor: vendor, vendorToken: token };
    } catch {}
    return { authVendor: null, vendorToken: null };
};

export const fetchVendors = createAsyncThunk(
    "vendors/fetchAll",
    async () => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors`);
        const vendors = await res.json();

        const map = {};
        vendors.forEach(v => { map[v.id] = v; });
        return map;
    },
    {
        condition: (_, { getState }) => {
            return getState().vendors.status !== "loading";
        },
    }
);

export const fetchVendorById = createAsyncThunk(
    "vendors/fetchById",
    async (id) => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/${id}`);
        return await res.json();
    }
);

export const fetchTopVendor = createAsyncThunk(
    "vendors/fetchTop",
    async () => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/top-vendor`);
        return await res.json();
    }
);

export const loginVendorUser = createAsyncThunk("vendors/login", async ({ email, password }) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Login failed");
    // Evict any user session
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.dispatchEvent(new Event("user-logout"));
    return data; // { vendor, token }
});

export const registerVendorUser = createAsyncThunk("vendors/register", async (vendorData) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(vendorData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Registration failed");
    // Evict any user session
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.dispatchEvent(new Event("user-logout"));
    return data; // { vendor, token }
});

export const fetchVendorAnalytics = createAsyncThunk(
    "vendors/fetchAnalytics",
    async ({ vendorId, token }) => {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/${vendorId}/analytics`, { headers });
        return await res.json();
    }
);

const { authVendor: storedAuthVendor, vendorToken: storedVendorToken } = loadVendorSession();

const initialState = {
    vendors: {},
    currentVendor: null,
    topVendor: null,
    authVendor: storedAuthVendor,
    vendorToken: storedVendorToken,
    status: "idle",
    error: null,
    vendorAnalytics: null,
    vendorAnalyticsStatus: "idle",
};

const applyVendorAuth = (state, vendor, token) => {
    state.authVendor = vendor;
    state.vendorToken = token;
    state.status = "succeeded";
    state.error = null;
    localStorage.setItem("vendor_token", token);
    localStorage.setItem("vendor_data", JSON.stringify(vendor));
};

const vendorSlice = createSlice({
    name: "vendors",
    initialState,
    reducers: {
        setVendors: setVendorsReducer,
        addVendor: addVendorReducer,
        removeVendor: removeVendorReducer,
        updateVendor: updateVendorReducer,
        setCurrentVendor: setCurrentVendorReducer,
        setVendorAuth: (state, action) => {
            applyVendorAuth(state, action.payload.vendor, action.payload.token);
        },
        clearVendorAuth: (state) => {
            state.authVendor = null;
            state.vendorToken = null;
            state.status = "idle";
            state.error = null;
            localStorage.removeItem("vendor_token");
            localStorage.removeItem("vendor_data");
        },
        updateVendorAuth: (state, action) => {
            state.authVendor = { ...state.authVendor, ...action.payload };
            localStorage.setItem("vendor_data", JSON.stringify(state.authVendor));
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVendors.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchVendors.fulfilled, (state, action) => {
                state.vendors = action.payload;
                state.status = "succeeded";
            })
            .addCase(fetchVendors.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchVendorById.fulfilled, (state, action) => {
                const vendor = action.payload;
                if (vendor?.id) {
                    state.vendors[vendor.id] = vendor;
                    state.currentVendor = vendor;
                }
            })
            .addCase(fetchTopVendor.fulfilled, (state, action) => {
                const vendor = action.payload;
                if (vendor?.id) {
                    state.vendors[vendor.id] = vendor;
                    state.topVendor = vendor;
                }
            })
            .addCase(loginVendorUser.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(loginVendorUser.fulfilled, (state, action) => {
                applyVendorAuth(state, action.payload.vendor, action.payload.token);
            })
            .addCase(loginVendorUser.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(registerVendorUser.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(registerVendorUser.fulfilled, (state, action) => {
                applyVendorAuth(state, action.payload.vendor, action.payload.token);
            })
            .addCase(registerVendorUser.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchVendorAnalytics.pending, (state) => {
                state.vendorAnalyticsStatus = "loading";
            })
            .addCase(fetchVendorAnalytics.fulfilled, (state, action) => {
                state.vendorAnalytics = action.payload;
                state.vendorAnalyticsStatus = "succeeded";
            })
            .addCase(fetchVendorAnalytics.rejected, (state) => {
                state.vendorAnalyticsStatus = "failed";
            });
    },
});

export const {
    setVendors, addVendor, removeVendor, updateVendor, setCurrentVendor,
    setVendorAuth, clearVendorAuth, updateVendorAuth,
} = vendorSlice.actions;
export default vendorSlice.reducer;
